import { Button } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import PerButton from '../../../components/custom_components/Button'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { generateTargetReportRequest } from '../../../graphql/reports/mutationsReport'
import { Country, FindCompaniesAndCountriesByCompanyManagerQuery } from '../../../types'
import { showNotification } from '../../../components/custom_components/Notification/notification'

type ReportsFilterHeaderProps = {
  companyId: string
  companyName: string
  country: Country
  generationInProgress: boolean
  generationDisabled: boolean
  // If hrSettings is true, companiesAsCompanyManager is useless.
  // If hrSettings is false, companiesAsCompanyManager is required.
  companiesAsCompanyManager?: FindCompaniesAndCountriesByCompanyManagerQuery
  setGenerationInProgress: (value: boolean) => void
  setPollingCount: (value: number) => void
  setModalGenerationDisabled: (value: boolean) => void
  hrSettings: boolean
}

export const CompanySettingsTargetReportsHeader: FC<ReportsFilterHeaderProps> = ({
  companyId,
  companyName,
  country,
  generationInProgress,
  generationDisabled,
  companiesAsCompanyManager,
  setGenerationInProgress,
  setPollingCount,
  setModalGenerationDisabled,
  hrSettings,
}) => {
  const [t] = useTranslation()

  const [expanded, setExpanded] = useState(true)
  const [targetReportGenerationDisabled, setTargetReportGenerationDisabled] = useState(false)

  useEffect(() => {
    setTargetReportGenerationDisabled(generationDisabled)
  }, [generationDisabled])

  const generateButtonVisible = () => {
    return (
      hrSettings ||
      companiesAsCompanyManager!.findCompaniesAndCountriesByCompanyManager.some(
        (el) =>
          ((el.companyId && el.companyId === companyId) || el.companyName === companyName) &&
          el.companyCountries.some((element) => element.country === country),
      )
    )
  }

  const generateReport = async () => {
    try {
      const res = await generateTargetReportRequest(companyId, country)
      if (!!res.errors && !!res.errors.length) {
        setGenerationInProgress(false)
        setTargetReportGenerationDisabled(true)
        showNotification({
          type: 'error',
          message: t('companySettingsView.targetReportsTab.header.notifications.error.title'),
          description: t('companySettingsView.targetReportsTab.header.notifications.error.description'),
        })
      } else if (!res.data!.generateTargetReportRequest.createTargetReport) {
        setTargetReportGenerationDisabled(true)
        setGenerationInProgress(false)
        setModalGenerationDisabled(true)
        showNotification({
          type: 'error',
          message: t('companySettingsView.targetReportsTab.header.notifications.error.title'),
          description: t('companySettingsView.targetReportsTab.header.notifications.error.generationDisabledText'),
        })
      } else if (!res.data!.generateTargetReportRequest.generationResult) {
        showNotification({
          type: 'warning',
          message: t('companySettingsView.targetReportsTab.header.notifications.warning.title'),
          description: t('companySettingsView.targetReportsTab.header.notifications.warning.description'),
        })
        setPollingCount(1)
      } else {
        showNotification({
          type: 'success',
          message: t('companySettingsView.targetReportsTab.header.notifications.success.title'),
          description: t('companySettingsView.targetReportsTab.header.notifications.success.description'),
        })
        setPollingCount(1)
      }
    } catch (error) {
      setGenerationInProgress(false)
      setTargetReportGenerationDisabled(true)
      showNotification({
        type: 'error',
        message: t('companySettingsView.targetReportsTab.header.notifications.error.title'),
        description: t('companySettingsView.targetReportsTab.header.notifications.error.description'),
      })
    }
  }

  return (
    <BackgroundBox rt rb mb="xs" noPadding>
      <PerFlexRow align="middle" justify="space-between" ml="m" mr="m" mt="s" mb="s">
        <PerFlexRow direction="column" align="middle">
          <div className="per-companySettings--targetReports-header" onClick={() => setExpanded(!expanded)}>
            <PerFlexRow align="middle" direction="row">
              <PerText appearance="tile-heading">{t('companySettingsView.targetReportsTab.header.heading')}</PerText>
              <Button icon={expanded ? 'up' : 'down'} className="per-companySettings--targetReports-header-button" />
            </PerFlexRow>
          </div>
        </PerFlexRow>
        <PerFlexRow direction="column">
          {generateButtonVisible() && (
            <PerButton
              text={t('companySettingsView.targetReportsTab.header.generateReport')}
              color={generationInProgress ? 'secondary' : 'primary'}
              before="report_fill"
              onClick={() => {
                setGenerationInProgress(true)
                generateReport()
              }}
              isProcessing={{
                active: generationInProgress && !(generationDisabled || targetReportGenerationDisabled),
                text: t('companySettingsView.targetReportsTab.header.generatingReport'),
                tooltip: String(
                  t('companySettingsView.targetReportsTab.header.generatingReportTooltip', { company: companyName, country: country }),
                ),
              }}
              disabled={generationDisabled || targetReportGenerationDisabled}
              tooltip={
                generationDisabled || targetReportGenerationDisabled
                  ? t('companySettingsView.targetReportsTab.header.generationDisabledTooltip')
                  : undefined
              }
            />
          )}
        </PerFlexRow>
      </PerFlexRow>
      <div
        className={
          expanded
            ? 'per-companySettings--targetReports-header-textContainer-expanding'
            : 'per-companySettings--targetReports-header-textContainer-shrinking'
        }
      >
        <PerFlexRow align="middle" justify="space-between" mb="m" ml="m" mr="m" mt="none">
          <PerText className="company-settings-target-report-header-text">
            {t('companySettingsView.targetReportsTab.header.infoText')}
          </PerText>
        </PerFlexRow>
      </div>
    </BackgroundBox>
  )
}
